import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { walletManager, type WalletInfo } from '../../utils/walletManager';

const WalletStatusBar: React.FC = () => {
  const { t } = useTranslation();
  const [walletInfo, setWalletInfo] = useState<WalletInfo | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Check initial connection status
    checkConnectionStatus();

    // Listen to wallet status change events
    const handleWalletStatusChange = (event: CustomEvent) => {
      console.log('Wallet status changed:', event.detail);
      const { isConnected, walletInfo } = event.detail;
      setIsConnected(isConnected);
      setWalletInfo(walletInfo);
    };

    window.addEventListener('walletStatusChanged', handleWalletStatusChange as EventListener);

    // Set periodic check (reduced frequency)
    const interval = setInterval(checkConnectionStatus, 10000);

    return () => {
      clearInterval(interval);
      window.removeEventListener('walletStatusChanged', handleWalletStatusChange as EventListener);
    };
  }, []);

  const checkConnectionStatus = async () => {
    const connected = await walletManager.isConnected();
    const info = walletManager.getWalletInfo();
    
    setIsConnected(connected);
    setWalletInfo(info);
    
    if (connected) {
      walletManager.refreshActivity();
    }
  };

  const handleDisconnect = () => {
    walletManager.disconnect();
    setIsConnected(false);
    setWalletInfo(null);
  };

  const handleReconnect = async () => {
    const result = await walletManager.connectWallet();
    if (result.success) {
      setIsConnected(true);
      setWalletInfo(result.walletInfo || null);
    } else {
      alert(`${t('wallet.connectionFailed')}: ${result.error}`);
    }
  };

  if (!isConnected || !walletInfo) {
    return (
      <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white py-2 px-4 text-center text-sm">
        <div className="flex items-center justify-center gap-2">
          <i className="fas fa-wallet"></i>
          <span>{t('wallet.notConnected')}</span>
          <button
            onClick={handleReconnect}
            className="ml-2 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
          >
            {t('wallet.connectWallet')}
          </button>
        </div>
      </div>
    );
  }

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'metamask': return '🦊';
      case 'coinbase': return '🔵';
      case 'trust': return '🔒';
      case 'binance': return '🟡';
      case 'imtoken': return '🔷';
      case 'bitget': return '🟠';
      case 'okx': return '⚫';
      case 'tokenpocket': return '🟣';
      case 'rainbow': return '🌈';
      case 'brave': return '🦁';
      case 'phantom': return '👻';
      default: return '💳';
    }
  };

  const getNetworkColor = (networkName: string) => {
    switch (networkName.toLowerCase()) {
      case 'ethereum': return 'bg-blue-500';
      case 'polygon': return 'bg-purple-500';
      case 'bsc': return 'bg-yellow-500';
      case 'arbitrum': return 'bg-cyan-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white py-2 px-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">{getProviderIcon(walletInfo.provider)}</span>
            <span className="font-medium">{t('wallet.connected')}</span>
          </div>
          
          <div className="hidden sm:flex items-center gap-4 text-sm">
            <div className="flex items-center gap-1">
              <span className="opacity-80">{t('wallet.address')}:</span>
              <span className="font-mono">{walletManager.getShortAddress()}</span>
            </div>
            
            <div className="flex items-center gap-1">
              <span className="opacity-80">{t('wallet.network')}:</span>
              <div className={`px-2 py-1 rounded-full text-xs font-medium ${getNetworkColor(walletInfo.networkName)}`}>
                {walletInfo.networkName}
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <span className="opacity-80">{t('wallet.status')}:</span>
              <span className="text-green-200">{walletManager.getConnectionStatus()}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <i className={`fas fa-chevron-${showDetails ? 'up' : 'down'} text-sm`}></i>
          </button>
          
          <button
            onClick={handleDisconnect}
            className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
          >
            {t('wallet.disconnect')}
          </button>
        </div>
      </div>

      {/* Details Panel */}
      {showDetails && (
        <div className="mt-3 pt-3 border-t border-white/20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="opacity-80">{t('wallet.fullAddress')}:</span>
              <div className="font-mono text-xs mt-1 break-all">
                {walletInfo.address}
              </div>
            </div>
            
            <div>
              <span className="opacity-80">{t('wallet.chainId')}:</span>
              <div className="font-mono text-xs mt-1">
                {walletInfo.chainId}
              </div>
            </div>
            
            <div>
              <span className="opacity-80">{t('wallet.connectionTime')}:</span>
              <div className="text-xs mt-1">
                {new Date(walletInfo.connectedAt).toLocaleString()}
              </div>
            </div>
            
            <div>
              <span className="opacity-80">{t('wallet.walletType')}:</span>
              <div className="text-xs mt-1 capitalize">
                {walletInfo.provider}
              </div>
            </div>
          </div>
          
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => navigator.clipboard.writeText(walletInfo.address)}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
            >
              <i className="fas fa-copy mr-1"></i>
              {t('wallet.copyAddress')}
            </button>
            
            <button
              onClick={() => window.open(`https://etherscan.io/address/${walletInfo.address}`, '_blank')}
              className="px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full text-xs font-medium transition-colors"
            >
              <i className="fas fa-external-link-alt mr-1"></i>
              {t('wallet.viewOnChain')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WalletStatusBar;